import React, { useState, useEffect, useMemo } from 'react';
import { TAutomationActionOption } from '@frontend/app/hooks';
import { GetNodeOptions_getNodeOptions_conditionOptions } from '@frontend/app/queries/types/GetNodeOptions';
import { AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import { X, Plus } from 'lucide-react';
import { tagColors } from '@frontend/app/hooks';
import { simpleHash } from '@frontend/app/containers/Settings/ProductFulfillment/utils';
import { AutomationNodeV2 } from '../../types';
import { Button } from '@/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Label } from '@/shadcn/components/ui/label';
import { Badge } from '@/shadcn/components/ui/badge';
import { CustomFieldUpdater, CustomFieldUpdateBlueprint } from './CustomField';
import { TagSelector } from './TagSelector';
import { GroupSelector } from './GroupSelector';
import { Avatar, AvatarFallback, AvatarImage } from '@/shadcn/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';

export const UPDATE_MEMBER_DATA_TYPES = {
  ADD_TAG: 'ADD_TAG',
  ADD_TO_GROUP: 'ADD_TO_GROUP',
  UPDATE_MEMBER_INFORMATION: 'UPDATE_MEMBER_INFORMATION',
} as const;

interface SelectedTag {
  fieldId: string | number;
  fieldValue: string;
}

interface SelectedGroup {
  fieldId: string | number;
  fieldValue: string;
}

// Generate initials from group name for avatar placeholder
const getGroupInitials = (groupName: string): string => {
  return groupName
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

const UPDATE_TYPE_LABELS = {
  [UPDATE_MEMBER_DATA_TYPES.ADD_TAG]: 'Add Tag',
  [UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP]: 'Add to Group',
  [UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION]: 'Update Custom Field',
} as const;

/**
 * UpdateMemberDataActionForm - Enhanced automation action form supporting:
 * 1. Traditional member data updates (ADD_TAG, ADD_TO_GROUP)
 * 2. Custom field updates (UPDATE_MEMBER_INFORMATION) with support for:
 *    - Number fields
 *    - Boolean fields (Yes/No)
 *    - Free text fields
 *    - Single select fields
 *    - Multi-select fields
 *    - Date fields
 *
 * Custom fields are dynamically loaded from the API based on CUSTOM_MEMBER_DATA group.
 */
interface UpdateMemberDataActionFormProps {
  selectedNode: AutomationNodeV2;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  actionOptions: TAutomationActionOption[];
  conditionOptions: GetNodeOptions_getNodeOptions_conditionOptions[];
  onSave: () => void;
  onRefreshNodeOptions?: () => Promise<any>;
}

export const UpdateMemberDataActionForm: React.FC<UpdateMemberDataActionFormProps> = ({
  selectedNode,
  onUpdateNode,
  actionOptions,
  conditionOptions,
  onSave,
  onRefreshNodeOptions,
}) => {
  const [updateType, setUpdateType] = useState<string>('');
  const [fieldValue, setFieldValue] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<SelectedTag[]>([]);
  const [isTagSelectorOpen, setIsTagSelectorOpen] = useState<boolean>(false);
  const [selectedGroups, setSelectedGroups] = useState<SelectedGroup[]>([]);
  const [isGroupSelectorOpen, setIsGroupSelectorOpen] = useState<boolean>(false);
  const [customFieldUpdates, setCustomFieldUpdates] = useState<CustomFieldUpdateBlueprint[]>([]);
  const [updateTypeValidationError, setUpdateTypeValidationError] = useState<string>('');
  const [fieldValueValidationError, setFieldValueValidationError] = useState<string>('');

  const updateMemberDataOption = useMemo(() => {
    return actionOptions?.find((option) => option.type === 'UPDATE_MEMBER_DATA');
  }, [actionOptions]);

  const tags = useMemo(() => updateMemberDataOption?.tags || [], [updateMemberDataOption]);
  const groups = useMemo(() => updateMemberDataOption?.groups || [], [updateMemberDataOption]);
  const updateTypes = useMemo(() => updateMemberDataOption?.updateTypes || [], [updateMemberDataOption]);

  // Initialize state from selectedNode metadata
  useEffect(() => {
    if (selectedNode && selectedNode.type === 'ACTION' && selectedNode.metadata) {
      // Map saved metadata back to form state
      const savedType = selectedNode.metadata.type;
      const savedFieldValue = selectedNode.metadata.fieldValue;
      const savedFieldId = selectedNode.metadata.fieldId;
      const savedValues = selectedNode.metadata.values;
      const savedCustomFields = selectedNode.metadata.customFields;

      // Set updateType directly from saved metadata (supports ADD_TAG, ADD_TO_GROUP, REMOVE_TAG, REMOVE_FROM_GROUP, UPDATE_MEMBER_INFORMATION)
      if (savedType && updateTypes.includes(savedType)) {
        setUpdateType(savedType);
      } else {
        // Fallback for legacy data format
        if (savedType === 'TAG') {
          setUpdateType(UPDATE_MEMBER_DATA_TYPES.ADD_TAG);
        } else if (savedType === 'GROUP') {
          setUpdateType(UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP);
        } else {
          setUpdateType('');
        }
      }

      // Handle both legacy single tag and new multi-tag formats
      if (savedType === UPDATE_MEMBER_DATA_TYPES.ADD_TAG) {
        if (savedValues && Array.isArray(savedValues)) {
          // New multi-tag format
          setSelectedTags(savedValues);
        } else if (savedFieldValue && savedFieldId) {
          // Legacy single tag format - convert to new format
          setSelectedTags([{
            fieldId: savedFieldId,
            fieldValue: savedFieldValue,
          }]);
        } else {
          setSelectedTags([]);
        }
      } else if (savedType === UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP) {
        // Handle both legacy single group and new multi-group formats
        if (savedValues && Array.isArray(savedValues)) {
          // New multi-group format
          setSelectedGroups(savedValues);
        } else if (savedFieldValue && savedFieldId) {
          // Legacy single group format - convert to new format
          setSelectedGroups([{
            fieldId: savedFieldId,
            fieldValue: savedFieldValue,
          }]);
        } else {
          setSelectedGroups([]);
        }
      } else {
        // For non-tag/group operations, use legacy fieldValue
        setFieldValue(savedFieldValue || '');
      }

      // Load saved custom field updates - check both new unified format (values) and legacy format (customFields)
      if (savedType === UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION) {
        if (savedValues && Array.isArray(savedValues)) {
          // New unified format - custom fields stored in values array
          // Ensure fieldId is always a string
          const normalizedValues = savedValues.map((value) => ({
            ...value,
            fieldId: String(value.fieldId)
          }));
          setCustomFieldUpdates(normalizedValues);
        } else if (savedCustomFields && Array.isArray(savedCustomFields)) {
          // Legacy format - custom fields stored in customFields array
          // Ensure fieldId is always a string
          const normalizedCustomFields = savedCustomFields.map((field) => ({
            ...field,
            fieldId: String(field.fieldId)
          }));
          setCustomFieldUpdates(normalizedCustomFields);
        } else {
          setCustomFieldUpdates([]);
        }
      }
    }
  }, [selectedNode, updateMemberDataOption, updateTypes]);

  const validateForm = () => {
    const noUpdateTypeError = 'Please select an update type';
    const noFieldValueError = 'Please select a value';
    const noCustomFieldsError = 'Please configure at least one custom field';
    const noError = '';

    const updateTypeError = !updateType ? noUpdateTypeError : noError;

    let fieldValueError = noError;
    if (updateType === UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION) {
      fieldValueError = customFieldUpdates.length === 0 ? noCustomFieldsError : noError;
    } else if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TAG) {
      fieldValueError = selectedTags.length === 0 ? 'Please select at least one tag' : noError;
    } else if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP) {
      fieldValueError = selectedGroups.length === 0 ? 'Please select at least one group' : noError;
    } else {
      fieldValueError = !fieldValue ? noFieldValueError : noError;
    }

    setUpdateTypeValidationError(updateTypeError);
    setFieldValueValidationError(fieldValueError);

    return updateTypeError === noError && fieldValueError === noError;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    // All UPDATE_MEMBER_DATA operations now use unified 'values' array format:
    // - ADD_TAG: values = [{ fieldId, fieldValue }, ...]
    // - ADD_TO_GROUP: values = [{ fieldId, fieldValue }, ...]
    // - UPDATE_MEMBER_INFORMATION: values = [{ fieldId, fieldValue }, ...]
    let updatedMetadata: any;

    if (updateType === UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION) {
      // Handle custom field updates using unified values format
      updatedMetadata = {
        subType: AutomationActionNodeSubType.UPDATE_MEMBER_DATA,
        resourceId: selectedNode.metadata.resourceId || 1,
        fromPreviousSender: selectedNode.metadata.fromPreviousSender ?? false,
        type: updateType,
        values: customFieldUpdates,
        // Parent-level demo keys required by backend
        fieldValue: 'test',
        fieldType: 1,
      };
    } else if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TAG) {
      // Handle multi-tag updates using new format
      updatedMetadata = {
        subType: AutomationActionNodeSubType.UPDATE_MEMBER_DATA,
        resourceId: selectedNode.metadata.resourceId || 1,
        fromPreviousSender: selectedNode.metadata.fromPreviousSender ?? false,
        type: updateType,
        values: selectedTags,
      };
    } else if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP) {
      // Handle multi-group updates using new format
      updatedMetadata = {
        subType: AutomationActionNodeSubType.UPDATE_MEMBER_DATA,
        resourceId: selectedNode.metadata.resourceId || 1,
        fromPreviousSender: selectedNode.metadata.fromPreviousSender ?? false,
        type: updateType,
        values: selectedGroups,
      };
    } else {
      // Handle traditional group updates (single selection)
      const selectedOption = getFieldValueOptions().find((option) => option.displayName === fieldValue);
      const fieldId = selectedOption?.value;
      const value = selectedOption?.displayName;

      updatedMetadata = {
        subType: AutomationActionNodeSubType.UPDATE_MEMBER_DATA,
        resourceId: selectedNode.metadata.resourceId || 1,
        fromPreviousSender: selectedNode.metadata.fromPreviousSender ?? false,
        type: updateType,
        fieldId,
        fieldValue: value,
      };
    }

    // Update the node metadata with the form values
    onUpdateNode(selectedNode.id, {
      metadata: updatedMetadata,
    });

    onSave();
  };

  const handleUpdateTypeChange = (value: string) => {
    setUpdateType(value);
    setFieldValue(''); // Reset field value when update type changes
    setSelectedTags([]); // Reset selected tags when update type changes
    setSelectedGroups([]); // Reset selected groups when update type changes
    setCustomFieldUpdates([]); // Reset custom field updates when update type changes
    setFieldValueValidationError('');
  };

  const getFieldValueOptions = () => {
    if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TAG) {
      return tags;
    } else if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP) {
      return groups;
    }
    return [];
  };

  const getFieldValuePlaceholder = () => {
    if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TAG) {
      return 'Select a tag';
    } else if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP) {
      return 'Select a group';
    }
    return 'Select an option';
  };

  const handleTagSelection = (newSelectedTags: SelectedTag[]) => {
    setSelectedTags(newSelectedTags);
    setFieldValueValidationError('');

    // Immediately update the node metadata to preserve state across re-renders
    const updatedMetadata = {
      ...selectedNode.metadata,
      type: UPDATE_MEMBER_DATA_TYPES.ADD_TAG,
      values: newSelectedTags,
      // Clear legacy single tag fields
      fieldValue: '',
      fieldId: undefined,
    };

    onUpdateNode(selectedNode.id, {
      metadata: updatedMetadata,
    });
  };

  const removeSelectedTag = (tagToRemove: SelectedTag) => {
    const updatedTags = selectedTags.filter((tag) =>
      tag.fieldId !== tagToRemove.fieldId || tag.fieldValue !== tagToRemove.fieldValue
    );

    setSelectedTags(updatedTags);

    // Immediately update the node metadata to preserve state across re-renders
    const updatedMetadata = {
      ...selectedNode.metadata,
      type: UPDATE_MEMBER_DATA_TYPES.ADD_TAG,
      values: updatedTags,
      // Clear legacy single tag fields
      fieldValue: '',
      fieldId: undefined,
    };

    onUpdateNode(selectedNode.id, {
      metadata: updatedMetadata,
    });
  };

  const handleGroupSelection = (newSelectedGroups: SelectedGroup[]) => {
    setSelectedGroups(newSelectedGroups);
    setFieldValueValidationError('');

    // Immediately update the node metadata to preserve state across re-renders
    const updatedMetadata = {
      ...selectedNode.metadata,
      type: UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP,
      values: newSelectedGroups,
      // Clear legacy single group fields
      fieldValue: '',
      fieldId: undefined,
    };

    onUpdateNode(selectedNode.id, {
      metadata: updatedMetadata,
    });
  };

  const removeSelectedGroup = (groupToRemove: SelectedGroup) => {
    const updatedGroups = selectedGroups.filter((group) =>
      group.fieldId !== groupToRemove.fieldId || group.fieldValue !== groupToRemove.fieldValue
    );

    setSelectedGroups(updatedGroups);

    // Immediately update the node metadata to preserve state across re-renders
    const updatedMetadata = {
      ...selectedNode.metadata,
      type: UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP,
      values: updatedGroups,
      // Clear legacy single group fields
      fieldValue: '',
      fieldId: undefined,
    };

    onUpdateNode(selectedNode.id, {
      metadata: updatedMetadata,
    });
  };

  // Check if update type is pre-configured (from sub-action selection)
  const isUpdateTypePreConfigured = updateType &&
    ['ADD_TAG', 'ADD_TO_GROUP', 'UPDATE_MEMBER_INFORMATION'].includes(updateType);

  const getGroupImage = (groupId: string | number) => {
    const id = String(groupId);
    const match = groups.find((group) => String(group.value) === id);
    return match?.image ?? '';
  };
  return (
    <div className="h-full flex flex-col">
      {/* Scrollable content area with bottom padding for buttons */}
      <div className="flex-1 overflow-y-auto space-y-4 px-1 pb-20">
        {/* Update Type section - only show if not pre-configured */}
        {!isUpdateTypePreConfigured && (
          <div className="space-y-2">
            <Label htmlFor="update-type">Update Type</Label>
            <Select
              value={updateType}
              onValueChange={handleUpdateTypeChange}
            >
              <SelectTrigger id="update-type">
                <SelectValue placeholder="Select update type" />
              </SelectTrigger>
              <SelectContent>
                {updateTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {UPDATE_TYPE_LABELS[type as keyof typeof UPDATE_TYPE_LABELS] || type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {updateTypeValidationError && (
              <p className="text-sm text-red-500">{updateTypeValidationError}</p>
            )}
          </div>
        )}

        {/* Tags Selection - Multi-select for ADD_TAG */}
        {updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TAG && (
          <div className="space-y-2">
            <div className="text-sm leading-normal text-grey-6">Assign selected tags to members</div>

            {/* Selected Tags Display */}
            {selectedTags.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-grey-1 border-none">
                {selectedTags.map((tag, index) => (
                  <Badge
                    key={`${tag.fieldId}-${index}`}
                    variant="secondary"
                    className="flex items-center gap-2 px-3 py-1 text-sm font-medium border-0 cursor-default"
                    style={{
                      backgroundColor: tagColors[simpleHash(tag.fieldValue) % tagColors.length]?.backgroundColor || '#e5e7eb',
                      color: '#374151'
                    }}
                    title={tag.fieldValue}
                  >
                    <span className="truncate max-w-[150px]">
                      {tag.fieldValue}
                    </span>
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-red-600 transition-colors flex-shrink-0"
                      onClick={() => removeSelectedTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            )}

            {/* Select Tags Button */}
            <div className="flex flex-col items-start gap-2.5 py-2 relative rounded-xl w-full">
              <div className="w-full">
                <Button
                  variant="outline"
                  onClick={() => setIsTagSelectorOpen(true)}
                  className="flex flex-col items-center gap-2 px-4 py-[5px] relative self-stretch w-full flex-[0_0_auto] z-0 bg-grey-0 rounded-lg border border-dashed border-grey-3 h-auto"
                >
                  <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                    <Plus className="h-3 w-3 text-grey-6" />
                    <span className="relative w-fit mt-[-1.00px] font-medium text-grey-6 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                      Select Tags
                    </span>
                  </div>
                </Button>
              </div>
            </div>

            {fieldValueValidationError && (
              <p className="text-sm text-red-500">{fieldValueValidationError}</p>
            )}
          </div>
        )}

        {/* Groups Selection - Multi-select for ADD_TO_GROUP */}
        {updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP && (
          <div className="space-y-3">
            <div className="text-sm leading-normal text-grey-6">Add members to the selected groups</div>

            {/* Selected Groups Display */}
            {selectedGroups.length > 0 && (
              <div className="bg-grey-1 rounded-xl p-3">
                <div className="space-y-2">
                  {selectedGroups.map((group, index) => (
                    <div key={`${group.fieldId}-${index}`} className="flex items-center gap-4 p-2 bg-grey-0 rounded-lg">
                      <div className="flex items-center gap-2 flex-1">
                        <Avatar className="w-6 h-6 rounded-lg bg-grey-2">
                          <AvatarImage src={getGroupImage(group.fieldId)} alt={group.fieldValue} />
                          <AvatarFallback className="bg-grey-2 text-grey-6 text-xs font-medium rounded-lg">
                            {getGroupInitials(group.fieldValue)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium text-grey-6">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-sm font-medium text-grey-6">
                                  {group.fieldValue.length > 10 ? `${group.fieldValue.substring(0, 25)}...` : group.fieldValue}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium">
                                {group.fieldValue}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 bg-white rounded-lg hover:bg-grey-1"
                        onClick={() => removeSelectedGroup(group)}
                      >
                        <X className="h-3 w-3 text-grey-6" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Select Groups Button */}
            <Button
              variant="outline"
              onClick={() => setIsGroupSelectorOpen(true)}
              className="flex flex-col items-center gap-2 px-4 py-[5px] relative self-stretch w-full flex-[0_0_auto] z-0 bg-grey-0 rounded-lg border border-dashed border-grey-3 h-auto"
            >
              <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                <Plus className="h-3 w-3 text-grey-6" />
                <span className="relative w-fit mt-[-1.00px] font-medium text-grey-6 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                  Select Groups
                </span>
              </div>
            </Button>
            {fieldValueValidationError && (
              <p className="text-sm text-red-500">{fieldValueValidationError}</p>
            )}
          </div>
        )}

        {/* Field Value section - show for non-tag/group operations */}
        {updateType && updateType !== UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION && updateType !== UPDATE_MEMBER_DATA_TYPES.ADD_TAG && updateType !== UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP && (
          <div className="space-y-2">
            <Label htmlFor="field-value">Group</Label>
            <Select
              value={fieldValue}
              onValueChange={setFieldValue}
            >
              <SelectTrigger id="field-value">
                <SelectValue placeholder={getFieldValuePlaceholder()} />
              </SelectTrigger>
              <SelectContent>
                {getFieldValueOptions().map((option) => (
                  <SelectItem key={option.value} value={option.displayName}>
                    {option.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldValueValidationError && (
              <p className="text-sm text-red-500">{fieldValueValidationError}</p>
            )}
          </div>
        )}

        {/* Custom Field Updater - only show when UPDATE_MEMBER_INFORMATION is selected */}
        {updateType === UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION && (
          <div className="space-y-2">
            <CustomFieldUpdater
              conditionOptions={conditionOptions}
              initialUpdates={customFieldUpdates}
              onUpdatesChange={(updates) => {
                setCustomFieldUpdates(updates);
              }}
            />
            {fieldValueValidationError && (
              <p className="text-sm text-red-500">{fieldValueValidationError}</p>
            )}
          </div>
        )}
      </div>

      {/* Action buttons - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-start p-4 border-t bg-white shadow-lg">
        <TooltipProvider>
          {(() => {
            // Check if we need at least one custom field for UPDATE_MEMBER_INFORMATION
            const needsCustomField = updateType === UPDATE_MEMBER_DATA_TYPES.UPDATE_MEMBER_INFORMATION;
            const hasNoCustomFields = needsCustomField && customFieldUpdates.length === 0;
            const isFormDisabled = hasNoCustomFields || !updateType;

            const button = (
              <Button
                onClick={handleSave}
                variant="outline"
                disabled={isFormDisabled}
                className="disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Done
              </Button>
            );

            // Show tooltip when disabled due to no custom fields
            if (hasNoCustomFields) {
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {button}
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Add a field to complete setup</p>
                  </TooltipContent>
                </Tooltip>
              );
            }

            return button;
          })()}
        </TooltipProvider>
      </div>

      {/* Tag Selector Modal */}
      <TagSelector
        isOpen={isTagSelectorOpen}
        onClose={() => setIsTagSelectorOpen(false)}
        onSave={handleTagSelection}
        availableTags={tags}
        initialSelectedTags={selectedTags}
        onRefreshTags={onRefreshNodeOptions}
      />

      {/* Group Selector Modal */}
      <GroupSelector
        isOpen={isGroupSelectorOpen}
        onClose={() => setIsGroupSelectorOpen(false)}
        onSave={handleGroupSelection}
        availableGroups={groups}
        initialSelectedGroups={selectedGroups}
        onGroupCreated={async (_newGroup) => {
          // Refresh the node options to get the updated groups list
          if (onRefreshNodeOptions) {
            await onRefreshNodeOptions();
          }
        }}
      />
    </div>
  );
};
